// components/lists/SalesList.tsx
import React from "react";
import { Search, Filter, Download, X } from "lucide-react";
import { Sale } from "../../../../types/features/administration/BuyAndSell";
import SaleItem from "./SaleItem";


interface FilterState {
  search: string;
  status: string;
  currency: string;
  dateRange: string;
  startDate: string;
  endDate: string;
}

interface SalesListProps {
  sales: Sale[];
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onSaleAction: (action: string, sale: Sale, data?: any) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  startIndex: number;
  endIndex: number;
  totalSales: number;
  loading?: boolean;
  vendeursMap: Record<string, any>;
}

const SalesList: React.FC<SalesListProps> = ({
  sales,
  filters,
  onFiltersChange,
  onSaleAction,
  currentPage,
  totalPages,
  onPageChange,
  startIndex,
  endIndex,
  totalSales,
  loading,
  vendeursMap,
}) => {
  const uniqueStatuses = [...new Set(sales.map((s) => s.status))];
  const uniqueCurrencies = [...new Set([...sales.map((s) => s.monaie_vente), ...sales.map((s) => s.monaie_achat)])];

  const hasActiveFilters =
    filters.search || filters.status || filters.currency || filters.dateRange || filters.startDate || filters.endDate;

  const clearFilters = () => {
    onFiltersChange({
      search: "",
      status: "",
      currency: "",
      dateRange: "",
      startDate: "",
      endDate: "",
    });
  };

  return (
    <div className="space-y-6">
      {/* Filtres et Recherche */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            Filtres
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-3 py-1 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Réinitialiser
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Recherche</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="ID, vendeur, acheteur..."
                  value={filters.search}
                  onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
                  className="pl-10 pr-4 py-2.5 w-full border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Statut</label>
              <select
                value={filters.status}
                onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium bg-white"
              >
                <option value="">Tous</option>
                {uniqueStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Devise</label>
              <select
                value={filters.currency}
                onChange={(e) => onFiltersChange({ ...filters, currency: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium bg-white"
              >
                <option value="">Toutes</option>
                {uniqueCurrencies.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </div>

          
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Date début</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => onFiltersChange({ ...filters, startDate: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Date fin</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => onFiltersChange({ ...filters, endDate: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">Période prédéfinie</label>
              <select
                value={filters.dateRange}
                onChange={(e) => onFiltersChange({ ...filters, dateRange: e.target.value })}
                className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium bg-white"
              >
                <option value="">Personnalisée</option>
                <option value="today">Aujourd'hui</option>
                <option value="yesterday">Hier</option>
                <option value="last7days">7 derniers jours</option>
                <option value="last30days">30 derniers jours</option>
                <option value="thisMonth">Ce mois</option>
                <option value="lastMonth">Mois dernier</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Résumé */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-700">
          Affichage <span className="text-blue-600">{sales.length}</span> vente(s) sur{" "}
          <span className="text-blue-600">{totalSales}</span>
        </div>
      </div>

      {/* Sales List */}
      <div className="space-y-3 relative">
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-70 z-10 flex items-center justify-center rounded-xl">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}
        {sales.length > 0 ? (
          sales.map((sale) => (
            <SaleItem key={sale.id} sale={sale} onAction={onSaleAction} vendeur={vendeursMap[sale.id_user_vendeur]} />
          ))
        ) : (
          <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <Filter className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-lg font-bold text-gray-900">Aucune vente trouvée</p>
              <p className="text-sm text-gray-600">Essayez de modifier vos critères de recherche ou vos filtres</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm font-semibold text-gray-700">
              Affichage {startIndex + 1}-{Math.min(endIndex, totalSales)} sur {totalSales} ventes
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                ← Précédent
              </button>
              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`w-10 h-10 rounded-lg font-semibold transition-colors ${
                        pageNum === currentPage
                          ? "bg-blue-600 text-white"
                          : "border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Suivant →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesList;
